/**
 * Pure helpers for LocationContext. Extracted so the location-resolution
 * logic can be unit-tested without rendering the provider (which needs a
 * live Supabase session). See src/lib/__tests__/location-utils.test.ts.
 */

import { showsFleetCommand } from '@/lib/roleViews';

export interface LocationLike {
  id: string;
  [key: string]: unknown;
}

/**
 * The minimum a location must expose for the company-scoping guard below.
 *
 * Deliberately NOT `LocationLike`: that carries an index signature, and a
 * plain interface (LocationContext's own `Location`) is not assignable to
 * one, so reusing it here would force a cast at the call site.
 */
export interface CompanyScopedLocation {
  id: string;
  company_id?: string;
}

export interface UserLocationLike {
  location_id: string;
  is_primary: boolean;
  location?: LocationLike | null;
}

/**
 * Whether the `user_locations` read produced a store the picker can actually
 * offer.
 *
 * Two different shapes mean "no": no membership rows at all, and rows whose
 * embedded `locations` came back NULL. The second is easy to miss -- the
 * query filters the embed (`locations.is_active`, `locations.company_id`),
 * and PostgREST answers a filtered to-one embed by nulling it rather than
 * dropping the parent row, so a person assigned only to a deactivated or
 * other-tenant store returns rows that resolveAvailableLocations then throws
 * away. Counting rows instead of usable locations made the caller skip its
 * fallbacks in exactly that case and hand back an empty picker.
 */
export function hasSelectableMembership(userLocations: UserLocationLike[]): boolean {
  return userLocations.some((ul) => Boolean(ul.location));
}

/**
 * Given the user's assigned locations and (for fleet roles) the full
 * company location list, decide which locations are selectable and which
 * one should be the default ("primary").
 *
 * Fleet roles (admin/corporate/super_admin — `canSwitchLocations`) see every
 * active company location; if that query came back empty (RLS hiccup, no
 * effective company id yet) we fall back to the user's own assigned
 * locations so the UI is never stranded with zero options. Non-fleet users
 * only ever see their assigned locations.
 *
 * The default is the user's `is_primary` assignment when present *and
 * selectable*, else the first available location — see the clamp below for
 * why that qualifier is load-bearing.
 */
export function resolveAvailableLocations(
  userLocations: UserLocationLike[],
  allCompanyLocations: LocationLike[],
  canSwitchLocations: boolean,
): { locations: LocationLike[]; primaryLocationId: string | null } {
  const primaryUserLocation = userLocations.find((ul) => ul.is_primary);
  const assignedLocations = userLocations
    .map((ul) => ul.location)
    .filter((loc): loc is LocationLike => Boolean(loc));

  let locations: LocationLike[];
  if (canSwitchLocations) {
    // Admin/Corporate: prefer the full company list, fall back to the
    // user's assigned locations if the admin query returned nothing.
    locations = allCompanyLocations.length > 0 ? allCompanyLocations : assignedLocations;
  } else {
    // Regular users: only their assigned locations.
    locations = assignedLocations;
  }

  // The default MUST be one of the selectable locations. `userLocations` and
  // `allCompanyLocations` are two independent queries, so a multi-company
  // account can hold an is_primary assignment in one tenant while the
  // selectable list belongs to another — the ops login's home assignment
  // (JIUFD's "Northgate") against a customer's stores. Handing that id back
  // as the default made the caller resolve a target it could not find in the
  // list, so it kept the previously-displayed location: the picker listed the
  // right company's stores while the trigger read "Northgate", and picking any
  // store fixed it permanently (the saved id then matched). Clamping here
  // means the default is always selectable.
  const primaryLocationId =
    (primaryUserLocation && locations.some((loc) => loc.id === primaryUserLocation.location_id)
      ? primaryUserLocation.location_id
      : null) ?? locations[0]?.id ?? null;

  return { locations, primaryLocationId };
}

/**
 * The location that should be displayed as current, given the ordered
 * preferences the caller has (PIN session > saved selection > primary) and
 * the list the user may actually choose from.
 *
 * Every candidate is checked against `locations`; the first that survives
 * wins, and anything unrecognized falls through to the first selectable
 * location. This is the last line of defense for the cross-tenant display
 * bug above: whatever the preference sources say, the id returned here is
 * either in the list or null, so `currentLocation` can never be a store the
 * user is not currently scoped to.
 */
export function resolveCurrentLocationId(
  locations: readonly CompanyScopedLocation[],
  preferredIds: readonly (string | null | undefined)[],
): string | null {
  for (const id of preferredIds) {
    if (id && locations.some((loc) => loc.id === id)) return id;
  }
  return locations[0]?.id ?? null;
}

/**
 * Whether a preserved `currentLocation` has to be dropped outright.
 *
 * refreshLocations deliberately keeps the previous location when a refresh
 * yields nothing, so a transient empty result doesn't blank every
 * location-scoped surface mid-session. That is right for a hiccup inside one
 * company and wrong across a company switch: a store belonging to the
 * PREVIOUS tenant must never stay on screen. Only clear when we positively
 * know the location belongs elsewhere — an unknown company on either side
 * keeps the current value.
 */
export function shouldClearStaleLocation(
  current: CompanyScopedLocation | null | undefined,
  effectiveCompanyId: string | null | undefined,
): boolean {
  if (!current || !effectiveCompanyId || !current.company_id) return false;
  return current.company_id !== effectiveCompanyId;
}

/**
 * Whether the current user is in the franchisor "All locations" fleet view.
 *
 * Gated on the same `showsFleetCommand` predicate the LocationSwitcher uses
 * to decide whether to *offer* the "All locations" entry at all
 * (admin/super_admin/corporate). LocationContext used to gate this on a
 * locally-defined admin/super_admin-only check that predated corporate
 * getting the fleet view (#725), so a corporate user could select "All
 * locations" in the switcher and have it silently do nothing — every
 * location-scoped surface, including the team communication composer, kept
 * using their single underlying currentLocation (ticket #1190: Vanessa's
 * post landed scoped to that one store instead of company-wide, so Lisa and
 * Oli — assigned to a different store — never saw it, while Ryan, who
 * happened to have that same store selected, could).
 */
export function resolveIsAllLocations(
  role: string | null | undefined,
  allLocationsRaw: boolean,
): boolean {
  return showsFleetCommand(role) && allLocationsRaw;
}

/**
 * Scope id for the location warm-start cache.
 *
 * MUST include the company, not just the profile. A multi-company operator
 * (an ops/support account with rows in `user_company_memberships`) keeps one
 * profile id across every tenant, so a profile-only key made the cache
 * company-blind: switching company hard-reloads the app, the warm read hit
 * the PREVIOUS company's entry, and the dashboard painted the old tenant's
 * stores. Composing the company in gives each tenant its own entry.
 *
 * Returns null when either half is missing so the caller takes a cache MISS
 * and falls through to the normal (correct, network) load — never a
 * half-scoped key that could read another company's list.
 *
 * Changing this key shape retires the old `warm:location:<profileId>`
 * entries: existing users get one cache miss on their next launch, then a
 * normal fetch. That is the intended, safe migration — nothing here is a
 * source of truth.
 */
export function locationWarmScope(
  profileId: string | null | undefined,
  companyId: string | null | undefined,
): string | null {
  if (!profileId || !companyId) return null;
  return `${profileId}:${companyId}`;
}

/**
 * Whether the locations already held in state belong to the company that is
 * active RIGHT NOW — i.e. whether the initialization effect may skip its
 * refresh.
 *
 * The old guard only asked "is there a current location and a non-empty
 * list?", which is true the moment the warm cache hydrates. For a company
 * switch that fires exactly at the wrong time: memberships load
 * asynchronously, so the first pass resolves the HOME company, hydrates and
 * re-caches its stores, and by the time the active company flips to the
 * switched-to tenant the guard sees "valid data" and skips the one refresh
 * that would have corrected it. The stale tenant's stores then stay on
 * screen for the whole session.
 *
 * We test the CURRENT location's company rather than requiring every entry
 * to match: a user may legitimately be assigned locations in more than one
 * company, and demanding uniformity there would re-trigger the refresh on
 * every pass. The displayed location is both the thing that was wrong and
 * the thing that must be right.
 *
 * A not-yet-resolved company id returns true (keep what we have) so the
 * async window doesn't thrash the list on every render.
 */
export function hasLocationDataForCompany(
  locations: readonly CompanyScopedLocation[],
  currentLocationId: string | null | undefined,
  effectiveCompanyId: string | null | undefined,
): boolean {
  if (!currentLocationId || locations.length === 0) return false;
  const current = locations.find((loc) => loc.id === currentLocationId);
  if (!current) return false;
  if (!effectiveCompanyId) return true;
  return current.company_id === effectiveCompanyId;
}

/**
 * Whether the page at `pathname` honours the header's "All locations" view.
 *
 * MainLayout shows "Showing <store>. "All locations" applies to the Dashboard
 * and Analytics; this page is per-store." everywhere else, so a page that
 * starts listing every store must be added here or that notice contradicts
 * it.
 *
 *  - Dashboard and Analytics (and their sub-routes) have fleet views.
 *  - The Tasks list (`/tasks` itself) lists every store's tasks under "All
 *    locations" (task-list-scope.ts, Ryan Green's portal request). Matched
 *    exactly: `/tasks/templates` is still per-store.
 */
export function routeHonorsAllLocations(pathname: string): boolean {
  if (['/dashboard', '/analytics'].some((r) => pathname.startsWith(r))) return true;
  return pathname === '/tasks' || pathname === '/tasks/';
}

/**
 * Whether MainLayout should render the "Showing <store>. "All locations"
 * applies to..." notice above the page at `pathname`.
 *
 * Not on pages that honour "All locations" (nothing to explain), and not on
 * the Messages inboxes (#1959, Erik Martinez). Those pages are fixed frames:
 * they cancel <main>'s padding with a negative margin and size themselves to
 * exactly viewport minus header, so they leave no room for anything above
 * them. With the notice rendered, the inbox slid up over half of it (the
 * "bar above the chat") and the extra height pushed the message box below
 * the bottom of the window. The notice is also simply untrue there:
 * conversations are company-wide, not per-store.
 */
export function routeShowsAllLocationsNotice(pathname: string): boolean {
  if (routeHonorsAllLocations(pathname)) return false;
  return !/^\/(schedules\/)?messages(\/|$)/.test(pathname);
}
